import { useState, useCallback } from 'react';
import { userService } from '../services/userService';
import type { User } from '../types';

interface UseUserProfileReturn {
  profile: User | null;
  isLoading: boolean;
  error: string | null;
  fetchProfile: (userId: number) => Promise<void>;
  clearProfile: () => void;
}

// Simple in-memory cache to avoid repeated API calls
const profileCache = new Map<number, { data: User; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useUserProfile = (): UseUserProfileReturn => {
  const [profile, setProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (userId: number) => {
    // Check cache first
    const cached = profileCache.get(userId);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      setProfile(cached.data);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userData = await userService.getUserById(userId);
      setProfile(userData);
      
      // Cache the result
      profileCache.set(userId, { data: userData, timestamp: now });
    } catch (err: any) {
      console.error('Failed to fetch user profile:', err);
      setError(err.message || 'Failed to load profile');
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearProfile = useCallback(() => {
    setProfile(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    profile,
    isLoading,
    error,
    fetchProfile,
    clearProfile,
  };
};